import { useContext, useEffect, useMemo } from "react";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { isSymbol } from "@shared/types/utils";
import {
  ApiGeneralDataResponse,
  GeneralAmountType,
  Limits,
  MainCoinAmountData,
  MainCoinsAvailableAmountData,
  QueryStatus,
} from "@shared/types";
import { useClientsData, useSymbolsMetadata } from "@shared/utils/hooks/misc";
import { MAX_FETCH_ALL_ACCOUNT_AMOUNTS } from "@shared/features/bots/BotForm/common/constants";

interface useCopyBotToWorkspaceProps {
  getMainCoinAmountData: (
    clientID?: number,
    mainCoin?: string,
    amountType?: GeneralAmountType
  ) => MainCoinAmountData | undefined;
  getMainCoinsAvailableAmountDataStatus: QueryStatus<
    ApiGeneralDataResponse<MainCoinsAvailableAmountData, Limits> | undefined
  >;
}

const useCopyBotToWorkspace = ({
  getMainCoinAmountData,
  getMainCoinsAvailableAmountDataStatus,
}: useCopyBotToWorkspaceProps) => {
  const { isCopyToWorkspace, symbols, botGeneralData, setAccount, account } =
    useContext(BotFormContext) || {};
  const { activeClientsData } = useClientsData();
  const { getMainCoin } = useSymbolsMetadata();
  const shouldFetchAllAccounts =
    !!activeClientsData &&
    activeClientsData?.length <= MAX_FETCH_ALL_ACCOUNT_AMOUNTS;

  const botExchange = useMemo(
    () =>
      isCopyToWorkspace && botGeneralData && "exchange" in botGeneralData
        ? botGeneralData.exchange
        : undefined,
    [isCopyToWorkspace, botGeneralData]
  );

  const filteredAccounts = useMemo(
    () =>
      isCopyToWorkspace
        ? activeClientsData?.filter(
            (account) => account.exchange === botExchange
          )
        : undefined,
    [isCopyToWorkspace, activeClientsData, botExchange]
  );

  const coin = useMemo(() => {
    if (!isSymbol(symbols)) {
      return getMainCoin(symbols?.[0], botExchange);
    }
  }, [symbols, botExchange]);

  const selectedAccount = useMemo(() => {
    if (!isCopyToWorkspace || getMainCoinsAvailableAmountDataStatus.isFetching)
      return undefined;
    if (shouldFetchAllAccounts) {
      return filteredAccounts?.slice().sort((a, b) => {
        const bAmount = getMainCoinAmountData(b.client_id, coin)?.amount || 0;
        const aAmount = getMainCoinAmountData(a.client_id, coin)?.amount || 0;
        return bAmount - aAmount;
      })?.[0];
    }
    return filteredAccounts?.[0];
  }, [
    isCopyToWorkspace,
    shouldFetchAllAccounts,
    filteredAccounts,
    getMainCoinsAvailableAmountDataStatus,
  ]);

  useEffect(() => {
    if (isCopyToWorkspace && selectedAccount && !account) {
      setAccount?.(selectedAccount);
    }
  }, [isCopyToWorkspace, selectedAccount, account]);
};

export default useCopyBotToWorkspace;
